<script lang="ts" setup>
  import { computed, ref } from 'vue'
  import { useNow } from '@vueuse/core'
  import { HugeiconsIcon } from '@hugeicons/vue'
  import ArrowDown01Icon from '@hugeicons/core-free-icons/ArrowDown01Icon'
  import ArrowRight01Icon from '@hugeicons/core-free-icons/ArrowRight01Icon'
  import { Download, RefreshCw } from 'lucide-vue-next'
  import { Button } from '~/components/ui/button'
  import { Collapsible } from '~/components/ui/collapsible'
  import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '~/components/ui/sheet'
  import ReportDownloadsList from '~/components/reports/downloads/ReportDownloadsList.vue'
  import ReportStatePanel from '~/components/reports/states/ReportStatePanel.vue'
  import type { UseReportRunsReturn } from '~/composables/reports/useReportRuns'
  import { useReportDownloadsStore } from '~/stores/reportDownloadsStore'
  import { bucketRunsByRecency, type DownloadBuckets } from '~/utils/reports/downloads-grouping'

  const props = defineProps<{
    runs: UseReportRunsReturn
  }>()

  const CLOCK_INTERVAL_MS = 60000

  const { t } = useI18n()
  const store = useReportDownloadsStore()
  const { isOpen } = storeToRefs(store)

  const now = useNow({ interval: CLOCK_INTERVAL_MS })
  const isArchiveOpen = ref(false)

  const buckets = computed<DownloadBuckets>(() => bucketRunsByRecency(props.runs.runs.value, now.value))
  const isEmpty = computed<boolean>(() => !props.runs.isLoading.value && props.runs.runs.value.length === 0)
</script>

<template>
  <Sheet v-model:open="isOpen">
    <SheetContent class="flex w-full flex-col gap-0 p-0 sm:max-w-lg" side="right">
      <SheetHeader class="border-b border-silver-light p-4 pr-16 text-left">
        <SheetTitle class="text-base font-medium text-primary">{{ t('reports.downloads.title') }}</SheetTitle>
        <SheetDescription class="text-xs text-muted-foreground">
          {{ t('reports.downloads.description') }}
        </SheetDescription>
      </SheetHeader>
      <div class="flex items-center justify-between gap-2 px-4 py-2 text-xs text-muted-foreground">
        <span v-if="runs.activeCount.value > 0">
          {{ t('reports.downloads.inProgress', { count: runs.activeCount.value }, runs.activeCount.value) }}
        </span>
        <span v-else>{{
          t('reports.downloads.count', { count: runs.runs.value.length }, runs.runs.value.length)
        }}</span>
        <Button :aria-label="t('reports.table.refresh')" size="xs" variant="ghost" @click="runs.refresh()">
          <RefreshCw class="size-3.5" />
          {{ t('reports.table.refresh') }}
        </Button>
      </div>
      <div class="min-h-0 flex-1 overflow-y-auto px-4 pb-4">
        <ReportStatePanel
          v-if="isEmpty"
          :description="t('reports.downloads.emptyDescription')"
          :icon="Download"
          :title="t('reports.downloads.empty')"
        />
        <div v-else class="flex flex-col gap-4">
          <section v-if="buckets.active.length > 0" class="flex flex-col gap-2">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {{ t('reports.downloads.sections.active') }}
            </h3>
            <ReportDownloadsList :entries="buckets.active" :runs="runs" />
          </section>

          <section class="flex flex-col gap-2">
            <h3 class="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {{ t('reports.downloads.sections.last7Days') }}
            </h3>
            <ReportDownloadsList v-if="buckets.recent.length > 0" :entries="buckets.recent" :runs="runs" />
            <p v-else class="rounded-lg border border-dashed border-silver p-3 text-xs text-muted-foreground">
              {{ t('reports.downloads.sections.noneRecent') }}
            </p>
          </section>

          <section v-if="buckets.archive.length > 0" class="flex flex-col gap-2">
            <button
              :aria-expanded="isArchiveOpen"
              class="flex w-full items-center gap-1.5 text-left text-xs font-semibold uppercase tracking-wide text-muted-foreground hover:text-primary"
              type="button"
              @click="isArchiveOpen = !isArchiveOpen"
            >
              <HugeiconsIcon
                :class="isArchiveOpen ? 'rotate-0' : '-rotate-90'"
                :icon="ArrowDown01Icon"
                :size="14"
                :stroke-width="2"
                aria-hidden="true"
                class="shrink-0 transition-transform duration-200"
              />
              {{ t('reports.downloads.sections.last30Days') }}
            </button>
            <Collapsible :is-open="isArchiveOpen">
              <ReportDownloadsList :entries="buckets.archive" :runs="runs" />
            </Collapsible>
          </section>
        </div>
      </div>
      <div class="flex flex-col gap-1 border-t border-silver-light px-4 py-3">
        <NuxtLink
          class="inline-flex w-fit items-center gap-1 text-xs+ font-medium text-primary hover:underline"
          to="/reports/downloads"
          @click="store.close()"
        >
          {{ t('reports.downloads.viewAll') }}
          <HugeiconsIcon :icon="ArrowRight01Icon" :size="14" :stroke-width="2" aria-hidden="true" />
        </NuxtLink>
        <p class="text-xs text-muted-foreground">
          {{
            buckets.olderCount > 0
              ? t('reports.downloads.olderCount', { count: buckets.olderCount }, buckets.olderCount)
              : t('reports.downloads.viewAllHint')
          }}
        </p>
      </div>
    </SheetContent>
  </Sheet>
</template>
